import React, { useMemo } from 'react';
import Banner from '../../../../../components/Banner';
import IconAiArrow from '../../../../../components/IconWrapper/arrow-ai';
import usePlatform from '../../../../../hooks/usePlatform';
import { getBannerOperationExtraAttr } from '../../../../../scripts/utils';
import './index.scss';
import '../../../../../components/AIP/aip-banner-wrap.scss';

type NoteTip = { value?: string; label?: string; img?: string };

type FeatureItem = {
  title: string;
  description: string;
};

const crmSectionDiagram1 = '/images/aip/customerrelations/unified-sales-workbench.png';
const crmSectionDiagram2 = '/images/aip/customerrelations/channel-collaboration-network.png';
const crmSectionDiagram3 = '/images/aip/customerrelations/online-transaction-portal.png';
const crmSectionDiagram4 = '/images/aip/customerrelations/intelligent-data-analysis.png';
const case01 = '/images/aip/Retail/case-01.png';
const bannerBg = '/images/aip/customerrelations/customerrelations-banner-bg.svg';

type FeatureSectionData = {
  title: string;
  subtitle: string;
  previewAlt: string;
  previewImage: string;
  items: FeatureItem[];
};

const featureSections: FeatureSectionData[] = [
  {
    title: '统一销售工作台',
    subtitle: '销售团队的“作战指挥中心”',
    previewAlt: '统一销售工作台示意图',
    previewImage: crmSectionDiagram1,
    items: [
      {
        title: '全流程线上化跟进',
        description:
          '支持从线索分配、商机推进、报价、合同到回款跟踪的全流程线上操作，所有行动记录可追溯。'
      },
      {
        title: '可视化销售漏斗',
        description: '直观呈现各阶段商机数量与金额，助力管理者清晰把脉销售管线健康度与预测业绩。'
      },
      {
        title: '嵌入式协同工具',
        description: '在客户与商机页面无缝集成沟通、任务、审批与文件共享，实现团队内的高效协同。'
      },
      {
        title: '个性化工作视图',
        description: '为销售代表与管理角色提供定制化仪表盘与工作清单，聚焦优先级事务，提升个人效率。'
      }
    ]
  },
  {
    title: '渠道协同网络',
    subtitle: '伙伴赋能的“业务延伸平台”',
    previewAlt: '渠道协同网络示意图',
    previewImage: crmSectionDiagram2,
    items: [
      {
        title: '商机与线索协同',
        description: '品牌方可向下游伙伴分发市场线索，并协同推进项目，实现信息同步与业务联动。'
      },
      {
        title: '标准化上报与跟进',
        description: '渠道伙伴通过门户标准化上报项目进展、竞争信息与需求，确保品牌方获取一线高质量信息。'
      },
      {
        title: '赋能与政策直达',
        description: '统一发布产品资料、培训内容、市场政策与促销通知，确保信息精准触达与赋能。'
      },
      {
        title: '嵌入式管理与考核',
        description: '透明化展示渠道业绩、目标完成率及各项过程指标，实现科学的管理与激励。'
      }
    ]
  },
  {
    title: '线上交易门户',
    subtitle: 'B2B业务的“自助数字前台”',
    previewAlt: '线上交易门户示意图',
    previewImage: crmSectionDiagram3,
    items: [
      {
        title: '自助化产品与订单',
        description: '客户可在线浏览产品、查看实时库存与专属价格，自助完成下单、支付与订单追踪。'
      },
      {
        title: '实时信息同步',
        description:
          '与后端业务系统（ERP/TERP）深度打通，实现库存、价格、物流状态的实时同步，保障信息准确。'
      },
      {
        title: '专属政策与合约',
        description: '支持基于客户等级、框架协议的自动化定价与促销展示，确保线上交易合规且高效。'
      },
      {
        title: '一站式对账结算',
        description: '集成订单、发票、付款及返利信息，为客户提供清晰、便捷的线上对账与结算体验。'
      }
    ]
  },
  {
    title: '智能数据分析',
    subtitle: '管理决策的“业绩透视镜” ',
    previewAlt: '智能数据分析示意图',
    previewImage: crmSectionDiagram4,
    items: [
      {
        title: '实时业绩仪表盘',
        description:
          '为管理者提供涵盖销售额、目标完成率、赢单率、客单价等关键指标的实时可视化看板。'
      },
      {
        title: '深度销售管线分析',
        description: '多维度分析销售漏斗转化率、各阶段停留时间，精准定位销售流程瓶颈与改进点。'
      },
      {
        title: '科学的业绩预测',
        description: '基于历史数据、商机阶段与权重，辅助进行更准确的销售收入预测，支撑业务规划。'
      },
      {
        title: '渠道与客户洞察',
        description: '分析渠道贡献、客户生命周期价值及行为，识别高价值伙伴与客户，优化资源投入策略。'
      }
    ]
  }
];

function FeatureSection({ section }: { section: FeatureSectionData }) {
  return (
    <section className="section">
      <div className="container">
        <h2 className="section-title">{section.title}</h2>
        <p className="section-subtitle feature-subtitle">{section.subtitle}</p>
        <div className="feature-split">
          <div className="feature-list">
            {section.items.map((item, index) => (
              <article key={`${section.title}-${item.title}`} className="feature-item feature-item-static">
                <div className="feature-head">
                  <span className="feature-title">{item.title}</span>
                  <span className="feature-index">#{index + 1}</span>
                </div>
                <p className="feature-desc">{item.description}</p>
              </article>
            ))}
          </div>
          <div className="feature-preview">
            <div className="feature-placeholder">
              <img
                className="feature-preview-image"
                src={section.previewImage}
                alt={section.previewAlt}
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Customerrelations() {
  const { isPc } = usePlatform() || {};

  const bannerConfig = useMemo(() => {
    const src = bannerBg;

    const note =
      '致力于解决销售过程中线索混乱、商机不透明等痛点，通过标准化销售流程与无缝协同，高效管理“从线索到回款”的全链路，将客户资源转化为持续增长的销售收入。';

    const noteTips: NoteTip[] = [
      { value: '+40%', label: '销售跟进效率' },
      { value: '+50%', label: '业绩预测准确率' },
      { value: '+60%', label: '渠道协同响应速度' }
    ];

    return {
      src,
      mobileSrc: bannerBg,
      title: '十大应用-客户关系',
      subheading: '',
      legend: '驱动从线索到回款的增长引擎',
      note,
      noteTips,
      forceShowNote: true,
      operation: [
        {
          text: '立即咨询',
          icon: <IconAiArrow />,
          extraAttr: getBannerOperationExtraAttr({
            jumpBO: { formBOId: 1, formBO: { id: 1 }, jumpContentType: 'FORM' }
          })
        }
      ]
    };
  }, []);

  return (
    <div className="customerrelations-page">
      <div className="aip-banner-wrap customerrelations-banner">
        <Banner
          showAdvantage={false}
          covered
          useArrow={false}
          useDots={false}
          size="large"
          type={'BANNER_TYPE.CAROUSEL'}
          name={typeof bannerConfig.title === 'string' ? bannerConfig.title : undefined}
          height={isPc ? 700 : 600}
          config={bannerConfig as any}
        />
      </div>

      <main>
        {featureSections.map((section) => (
          <FeatureSection key={section.title} section={section} />
        ))}

        {/* 行业实践案例 - 暂时隐藏 */}
        {/* <section className="section" id="cases">
          <div className="container">
            <h2 className="section-title">行业实践案例</h2>
            <p className="section-subtitle feature-subtitle">智慧零售运营中枢实践案例</p>
            <div className="cases">
              <a
                className="case-link"
                href="https://terminus.io/case-detail/2009001"
                target="_blank"
                rel="noopener noreferrer"
              >
                <article className="case-card">
                  <span className="case-badge">案例 01</span>
                  <div className="case-body">
                    <h3 className="case-title">智慧零售全渠道业务增长实践</h3>
                    <p className="case-desc">
                      通过一盘货、统一订单中心和客户引擎协同联动，帮助企业提升库存周转效率、履约人效与会员复购表现。
                    </p>
                    <div className="case-illustration">
                      <img className="case-illustration-img" src={case01} alt="零售案例配图" loading="lazy" />
                    </div>
                  </div>
                </article>
              </a>
            </div>
          </div>
        </section> */}
      </main>
    </div>
  );
}
